import React, { createContext, useContext, useState, useCallback } from "react";
import { searchService, SearchResultProject } from "../services/searchService";

interface GlobalSearchContextType {
  globalSearchTerm: string;
  setGlobalSearchTerm: (term: string) => void;
  globalSearchResults: SearchResultProject[];
  isGlobalSearchLoading: boolean;
  globalSearchError: string | null;
  performGlobalSearch: (term: string) => Promise<void>;
  clearGlobalSearch: () => void;
}

const GlobalSearchContext = createContext<GlobalSearchContextType | undefined>(
  undefined
);

export const useGlobalSearch = () => {
  const context = useContext(GlobalSearchContext);
  if (context === undefined) {
    throw new Error(
      "useGlobalSearch must be used within a GlobalSearchProvider"
    );
  }
  return context;
};

export const GlobalSearchProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [globalSearchTerm, setGlobalSearchTerm] = useState("");
  const [globalSearchResults, setGlobalSearchResults] = useState<
    SearchResultProject[]
  >([]);
  const [isGlobalSearchLoading, setIsGlobalSearchLoading] = useState(false);
  const [globalSearchError, setGlobalSearchError] = useState<string | null>(
    null
  );

  const performGlobalSearch = useCallback(async (term: string) => {
    if (!term.trim()) {
      setGlobalSearchResults([]);
      setGlobalSearchError(null);
      return;
    }

    setIsGlobalSearchLoading(true);
    setGlobalSearchError(null);

    try {
      const response = await searchService.globalSearch(term);

      if (response.success && response.data) {
        setGlobalSearchResults(response.data.projects);
      } else {
        setGlobalSearchError(response.message || "Search failed");
        setGlobalSearchResults([]);
      }
    } catch (error) {
      console.error("Global search error:", error);
      setGlobalSearchError("An error occurred while searching");
      setGlobalSearchResults([]);
    } finally {
      setIsGlobalSearchLoading(false);
    }
  }, []);

  const clearGlobalSearch = useCallback(() => {
    setGlobalSearchTerm("");
    setGlobalSearchResults([]);
    setGlobalSearchError(null);
  }, []);

  const value = {
    globalSearchTerm,
    setGlobalSearchTerm,
    globalSearchResults,
    isGlobalSearchLoading,
    globalSearchError,
    performGlobalSearch,
    clearGlobalSearch,
  };

  return (
    <GlobalSearchContext.Provider value={value}>
      {children}
    </GlobalSearchContext.Provider>
  );
};
